/* eslint-disable sort-keys */
import {NinchatEnvironmentConfigTheme} from 'embed/types'

import {
    globalCss,
} from '@stitches/react'

export const embedGlobalStyles = (theme: NinchatEnvironmentConfigTheme) => {
    const {customFonts} = theme
    return globalCss({
        '@font-face': [
            ...customFonts?.map((font) => ({
                fontFamily: font.family || '',
                fontStyle: font.style || 'normal',
                fontWeight: font.weight || 'normal',
                src: font.src,
                ...font.ascentOverride ? {ascentOverride: font.ascentOverride} : {},
                ...font.descentOverride ? {descentOverride: font.descentOverride} : {},
                ...font.lineGapOverride ? {lineGapOverride: font.lineGapOverride} : {},
            })) || [],
        ],
        'html, body, #root': {height: '100%'},
        'body': {
            'button, label, input, textarea, select': {
                fontFamily: '$embedBody',
                color: '$embedBody',
                lineHeight: '$embedBody',
                fontSize: '$embedBody',
                fontWeight: '$embedBody',
                // custom theme mapping keywords doesent work on global styles, need to get proper style directly from theme
                fontStyle: theme?.fontStyle?.embedBody || 'normal',
                fontVariant: theme?.fontVariant?.embedBody || 'normal',
            },
            'fontFamily': '$embedBody',
            'color': '$embedBody',
            'lineHeight': '$embedBody',
            'fontSize': '$embedBody',
            'fontWeight': '$embedBody',
            'background': '$embedBodyBackground',
            // custom theme mapping keywords doesent work on global styles, need to get proper style directly from theme
            'fontStyle': theme?.fontStyle?.embedBody || 'normal',
            'fontVariant': theme?.fontVariant?.embedBody || 'normal',
        },
    })()
}

export const embedWrapper = {
    'height': '100%',
    'h1': {
        color: '$embedBodyHeading1',
        fontFamily: '$embedBodyHeading1',
        lineHeight: '$embedBodyHeading1',
        fontSize: '$embedBodyHeading1',
        fontWeight: '$embedBodyHeading1',
        fontStyle: '$embedBodyHeading1',
        fontVariant: '$embedBodyHeading1',
    },
    'h2': {
        color: '$embedBodyHeading2',
        fontFamily: '$embedBodyHeading2',
        lineHeight: '$embedBodyHeading2',
        fontSize: '$embedBodyHeading2',
        fontWeight: '$embedBodyHeading2',
        fontStyle: '$embedBodyHeading2',
        fontVariant: '$embedBodyHeading2',
    },
    'h3': {
        color: '$embedBodyHeading3',
        fontFamily: '$embedBodyHeading3',
        lineHeight: '$embedBodyHeading3',
        fontSize: '$embedBodyHeading3',
        fontWeight: '$embedBodyHeading3',
        fontStyle: '$embedBodyHeading3',
        fontVariant: '$embedBodyHeading3',
    },
    'h4': {
        color: '$embedBodyHeading4',
        fontFamily: '$embedBodyHeading4',
        lineHeight: '$embedBodyHeading4',
        fontSize: '$embedBodyHeading4',
        fontWeight: '$embedBodyHeading4',
        fontStyle: '$embedBodyHeading4',
        fontVariant: '$embedBodyHeading4',
    },
    'h5': {
        color: '$embedBodyHeading5',
        fontFamily: '$embedBodyHeading5',
        lineHeight: '$embedBodyHeading5',
        fontSize: '$embedBodyHeading5',
        fontWeight: '$embedBodyHeading5',
        fontStyle: '$embedBodyHeading5',
        fontVariant: '$embedBodyHeading5',
    },
    'h6': {
        color: '$embedBodyHeading6',
        fontFamily: '$embedBodyHeading6',
        lineHeight: '$embedBodyHeading6',
        fontSize: '$embedBodyHeading6',
        fontWeight: '$embedBodyHeading6',
        fontStyle: '$embedBodyHeading6',
        fontVariant: '$embedBodyHeading6',
    },
    'strong, b': {fontWeight: '$embedBodyBold'},
    'button': {transition: 'all 0.15s'},
}

export const embedWrapperInner = {
    height: '100%',
    overflow: 'auto',
}

export const embedComponentsWrapper = {
    boxSizing: 'border-box',
    height: '100%',
    variants: {
        isChannel: {
            true: {
                paddingTop: 0,
                paddingBottom: 0,
            },
        },
        isQueue: {true: {paddingTop: 0}},
        isContainerWithHeader: {true: {paddingTop: '$$headerHeight'}},
    },
    compoundVariants: [
        {
            css: {paddingTop: '$$headerHeight'},
            isChannel: true,
            isContainerWithHeader: true,
        },
        {
            css: {paddingTop: '$$headerHeight'},
            isQueue: true,
            isContainerWithHeader: true,
        },
    ],
}

export const embedChannelWrapper = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
}

export const channelWrapper = {
    '> div': {height: '100%'},
    'height': '100%',
    'position': 'relative',
    'zIndex': 1,
    'variants': {
        isChatOpen: {true: {}},
        activeVideoChannelId: {true: {zIndex: 'auto'}},
    },
}

export const heroWrapper = {
    position: 'relative',
    zIndex: 2,
    variants: {
        heroFullVisible: {true: {'+ div': {marginTop: '-10px'}}},
        isVideoChannel: {true: {'+ div': {marginTop: '-10px'}}},
        heroFullAbsoluteVisible: {
            true: {
                'position': 'absolute',
                'left': 0,
                'right': 0,
                '+ div': {marginTop: 0},
                'zIndex': '$heroFullAbsolute',
            },
        },
    },
}

export const embedQueue = {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    width: '100%',
}

export const embedQueueAdditionalContent = {
    margin: '$inQueueText',
    textAlign: 'center',
    img: {maxWidth: '100%'},
}

export const jitsiWrapper = {
    position: 'absolute',
    width: '100%',
    top: 0,
    bottom: 0,
    variants: {isContainerWithHeader: {true: {top: '$$headerHeight'}}},
}

export const newMessages = {
    position: 'absolute',
    top: '20px',
    zIndex: '$newMessagesVideoButton',
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
}

export const newMessagesButton = {
    'display': 'flex',
    'alignItems': 'center',
    'gap': '8px',
    'padding': '15px',
    'borderRadius': '10px',
    'backgroundColor': '$newMessagesBackground',
    'color': '$newMessagesText',
    'border': 'none',
    '> div': {
        '&:first-child': {lineHeight: 0},
        '&:nth-child(2)': {width: '100%'},
        '&:last-child': {lineHeight: 0},
    },
}

export const newMessagesCloseButton = {
    display: 'flex',
    alignItems: 'center',
    background: 'transparent',
    border: 'none',
    color: '#FFFFFF',
    width: '30px',
    height: '30px',
}
